import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import {
  DailyReport, DailyReportActivity, DailyReportEmployee, DailyReportEquipment, DailyReportTower
} from '../../types/dailyReportTypes';
import DailyReportTable from '../../components/dailyReportTable';
import { Project } from '../../types/projectTypes';
import { getProjects, getActiveProjects } from '../../api/projectApi';
import { getProjectActivities } from '../../api/activityApi';
import { ProjectActivity } from '../../types/ActivityTypes';
import { createDailyReport, getDailyReportDetails, getDailyReports } from '../../api/dailyReportApi';
import { getEmployees } from '../../api/employeeApi';
import { Employee } from '../../types/EmployeeTypes';
import ActivityDetails from '../../components/ActivityDetails';
import DailyReportForm from '../../components/DailyReportForm';
import { useNavigate } from 'react-router-dom';

// NORMALIZADOR DE TEXTO
const normalizeText = (text: string | undefined | null) => {
  if (!text) return "";
  return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
};

const DailyReports: React.FC = () => {
  const [reports, setReports] = useState<DailyReport[]>([]);
  const [filteredReports, setFilteredReports] = useState<DailyReport[]>([]);
  const [showCreateReportForm, setShowCreateReportForm] = useState(false);

  const [projects, setProjects] = useState<Project[]>([]);       // Todos (Historial)
  const [activeProjects, setActiveProjects] = useState<Project[]>([]); // Solo Activos (ComboBox)

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<DailyReport | null>(null);
  const [relatedActivities, setRelatedActivities] = useState<any[]>([]);

  const [assignedEmployees, setAssignedEmployees] = useState(new Set<string>());
  const [assignedEquipment, setAssignedEquipment] = useState(new Set<number>());

  const [showNoActivitiesMessage, setShowNoActivitiesMessage] = useState(false);

  const [activityTotals, setActivityTotals] = useState<Record<number, { hh: number; hm: number; production: Record<number, number> }>>({});
  const [viewCreate, setViewCreate] = useState<string>();

  const navigate = useNavigate();

  useEffect(() => {
    if (selectedReport && relatedActivities.length === 0) {
      setShowNoActivitiesMessage(true);
      const timer = setTimeout(() => {
        setShowNoActivitiesMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [selectedReport, relatedActivities]);

  useEffect(() => {
    fetchReports();
    fetchProjects();
    fetchActiveProjects();
    fetchEmployees();
  }, []);

  const handleOpen = () => {
    navigate("/reporte-diario/Crear");
  };

  const handleClose = () => {
    setOpen(false);
    fetchReports();
  };

  const fetchReports = async () => {
    try {
      const data = await getDailyReports();
      const sortedData = data.sort((a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      setReports(sortedData);
    } catch (error) {
      console.error('Error al obtener los reportes diarios:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const projectsData = await getProjects();
      if (Array.isArray(projectsData)) {
        setProjects(projectsData);
      } else {
        console.error('Los datos de proyectos no son un array:', projectsData);
      }
    } catch (error) {
      console.error('Error al obtener los proyectos:', error);
    }
  };

  const fetchActiveProjects = async () => {
    try {
      const activeProjectsData = await getActiveProjects();
      if (Array.isArray(activeProjectsData)) {
        setActiveProjects(activeProjectsData);
      }
    } catch (error) {
      console.error('Error al obtener proyectos activos:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const employeesData = await getEmployees();
      if (Array.isArray(employeesData)) {
        setEmployees(employeesData);
      } else {
        console.error('Los datos de empleados no son un array:', employeesData);
      }
    } catch (error) {
      console.error('Error al obtener los empleados:', error);
    }
  };

  const handleFilter = (filteredData: DailyReport[]) => {
    setFilteredReports(filteredData);
  };

  const toggleCreateReportForm = () => {
    setShowCreateReportForm(!showCreateReportForm);
  };

  const handleAddDailyReport = async (
        newDailyReport: Omit<DailyReport, 'id'>,
        productionData: DailyReportTower[] | DailyReportTower[][],
        personnelData: DailyReportEmployee[] | DailyReportEmployee[][],
        equipmentData: DailyReportEquipment[] | DailyReportEquipment[][],
        addedActivities: any[],
        activityTotals: Record<number, { hh: number; hm: number; production: Record<number, number> }>,
        subcontractData?: any[]
      ) => {
        try {
          // ... (Pasos 1 al 6 se mantienen igual: preparación de datos) ...
          const totalHh = Object.values(activityTotals).reduce((sum: number, { hh }: any) => sum + (hh || 0), 0);
          const totalHm = Object.values(activityTotals).reduce((sum: number, { hm }: any) => sum + (hm || 0), 0);

          const d = new Date(newDailyReport.date);
          const pad = (num: number) => num.toString().padStart(2, '0');
          const safeDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;

          const dataUser = localStorage.getItem("user");
          let userObj: any = {};
          try { userObj = dataUser ? JSON.parse(dataUser) : {}; } catch (e) { console.warn(e); }
          const safeIdentification = userObj.identificationNum || newDailyReport.identificationNum || "S/N";

          const reportHeader = {
            ...newDailyReport,
            date: safeDate,
            identificationNum: safeIdentification,
            totalHh: totalHh,
            totalHm: totalHm,
            id: 0
          };

          const flatProduction = Array.isArray(productionData) ? productionData.flat() : [];
          const flatPersonnel = Array.isArray(personnelData) ? personnelData.flat() : [];
          const flatEquipment = Array.isArray(equipmentData) ? equipmentData.flat() : [];

          const payload = {
            dailyReport: reportHeader,
            activities: addedActivities,
            productionRows: flatProduction,
            personnelRows: flatPersonnel,
            equipmentRows: flatEquipment,
            subcontractRows: Array.isArray(subcontractData) ? subcontractData.flat() : []
          };

          const response = await createDailyReport(payload);

          if (response.dailyReport) {
             setReports((prevReports) => {
               const newReport = response.dailyReport;
               const newReports = [...prevReports, newReport];
               return newReports.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
             });
          }
            return response;

        } catch (error) {
          console.error('Error al crear el reporte diario', error);
          throw error;
        }
      };

  const handleSelectReport = async (report: DailyReport | null) => {
    setRelatedActivities([]);
    setActivityTotals({});
    setAssignedEmployees(new Set());
    setAssignedEquipment(new Set());

    setSelectedReport(report);

    if (report && report.id) {
      try {
        // El detalle del reporte es lo crítico; si falla getProjectActivities solo se pierde el orden,
        // no debe impedir ver las actividades del reporte (incluidos borradores).
        const reportDetails = await getDailyReportDetails(report.id);
        let projectActivities: any[] = [];
        try {
          projectActivities = await getProjectActivities(report.idProject);
        } catch (e) {
          console.warn('No se pudieron cargar las actividades del proyecto (orden):', e);
        }

        const reportDetailsArray = Array.isArray(reportDetails) ? reportDetails : [reportDetails];
        const orderMap = new Map<string, number>();

        if (Array.isArray(projectActivities)) {
          projectActivities.forEach((pa: ProjectActivity) => {
            const orderVal = pa.order ?? 9999;
            if (pa.activity && pa.activity.id) {
                orderMap.set(`ID:${pa.activity.id}`, orderVal);
            }
            if (pa.activity && pa.activity.name) {
                const nameKey = normalizeText(pa.activity.name);
                if (!orderMap.has(`NAME:${nameKey}`)) {
                    orderMap.set(`NAME:${nameKey}`, orderVal);
                }
            }
          });
        }

        const formattedActivities = reportDetailsArray.map(detail => ({
          activity: detail.activity,
          workFronts: (detail.workFrontDetails || []).map((front: any) => ({
            frontNumber: front.workFront?.frontNumber ?? front.frontNumber,
            productionRows: front.production,
            personnelRows: front.personnel,
            equipmentRows: front.equipment,
            subcontractRows: front.subcontracts,
            observations: front.workFront?.observations
          })),
        }));

        formattedActivities.sort((a, b) => {
          const getOrder = (item: any) => {
            const masterId = item.activity?.activity?.id;
            if (masterId) {
                const idKey = `ID:${masterId}`;
                if (orderMap.has(idKey)) return orderMap.get(idKey)!;
            }
            const name = item.activity?.activityName || item.activity?.activity?.name;
            if (name) {
                const nameKey = `NAME:${normalizeText(name)}`;
                if (orderMap.has(nameKey)) return orderMap.get(nameKey)!;
            }
            return 9999;
          };
          return getOrder(a) - getOrder(b);
        });

        setRelatedActivities(formattedActivities);
        setViewCreate("view");

      } catch (error) {
        console.error('Error al obtener los detalles del reporte diario:', error);
      }
    } else {
      setSelectedReport(null);
      setRelatedActivities([]);
      setShowNoActivitiesMessage(false);
      setActivityTotals({});
      setAssignedEmployees(new Set());
      setAssignedEquipment(new Set());
    }
  };

  const handleCreateReport = () => navigate('/reporte-diario/Crear');
  const handleActivityChange = (index: number, updatedActivity: any) => {
    setRelatedActivities((prev) => {
      const newActs = [...prev];
      newActs[index] = updatedActivity;
      return newActs;
    });
  };
  const handleAssignEmployee = (rut: string) => setAssignedEmployees((prev) => new Set(prev).add(rut));
  const handleAssignEquipment = (id: number) => setAssignedEquipment((prev) => new Set(prev).add(id));
  const unassignEmployee = (rut: string) => setAssignedEmployees((prev) => { const u = new Set(prev); u.delete(rut); return u; });
  const unassignEquipment = (id: number) => setAssignedEquipment((prev) => { const u = new Set(prev); u.delete(id); return u; });
  const handleUpdateTotals = (index: number, hh: number, hm: number, prod: Record<number, number>) => setActivityTotals((prev) => ({ ...prev, [index]: { hh, hm, production: prod } }));

  const handleDeleteActivity = (indexToRemove: number) => {
      const activityToDelete = relatedActivities[indexToRemove];
      if (activityToDelete && activityToDelete.workFronts) {
          activityToDelete.workFronts.forEach((front: any) => {
            front.personnelRows?.forEach((row: any) => row.employee?.rut && unassignEmployee(row.employee.rut));
            front.equipmentRows?.forEach((row: any) => row.equipment?.id && unassignEquipment(row.equipment.id));
          });
      }
      setRelatedActivities(prev => prev.filter((_, i) => i !== indexToRemove));
      setActivityTotals(prev => { const n = { ...prev }; delete n[indexToRemove]; return n; });
  };

  return (
    <Box sx={{ marginTop: 3 }}>
      {showCreateReportForm ? (
        <DailyReportForm
            projects={activeProjects}
            onCancel={toggleCreateReportForm}
            onAddDailyReport={handleAddDailyReport}
            assignedEmployees={assignedEmployees}
            assignedEquipment={assignedEquipment}
            onAssignEmployee={handleAssignEmployee}
            onAssignEquipment={handleAssignEquipment}
            unassignEmployee={unassignEmployee}
            unassignEquipment={unassignEquipment}
        />
      ) : (
        <>
          <Grid sx={{ p: 2 }}>
            <Button variant="contained" color="primary" onClick={toggleCreateReportForm} >
              Crear reporte
            </Button>
          </Grid>

          <DailyReportTable
            reports={reports}
            projects={projects}
            activeProjects={activeProjects}
            employees={employees}
            onFilter={handleFilter}
            onSelectReport={handleSelectReport}
            selectedReport={selectedReport}
            detailContent={
              selectedReport && relatedActivities.length === 0 ? (
                <Typography sx={{ p: 2 }}>No hay actividades para este reporte.</Typography>
              ) : (
                relatedActivities.length > 0 && (
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', paddingLeft: 2 }}>
                      Detalles del Reporte Seleccionado
                      {selectedReport && selectedReport.date && (
                        <Box sx={{ ml: 2, p: 1, bgcolor: 'lightgray', borderRadius: 1, display: 'inline-block', fontSize: '0.9rem', fontWeight: 'normal' }}>
                          {new Date(selectedReport.date).toLocaleDateString()}
                        </Box>
                      )}
                    </Typography>
                    {relatedActivities.map((relatedActivity, index) => (
                      <ActivityDetails
                        key={`${selectedReport?.id || 'new'}-${relatedActivity.activity.id || index}`}
                        index={index}
                        onRemove={() => handleDeleteActivity(index)}
                        assignedEmployees={assignedEmployees}
                        assignedEquipment={assignedEquipment}
                        onAssignEmployee={handleAssignEmployee}
                        onAssignEquipment={handleAssignEquipment}
                        unassignEmployee={unassignEmployee}
                        unassignEquipment={unassignEquipment}
                        towers={[]}
                        employees={employees}
                        equipmentList={[]}
                        activity={relatedActivity.activity}
                        workFronts={relatedActivity.workFronts}
                        project={selectedReport?.idProject}
                        onChange={(updatedActivity) => handleActivityChange(index, updatedActivity)}
                        onUpdateTotals={handleUpdateTotals}
                        status={viewCreate}
                        onChangeStatus={(newStatus) => setViewCreate(newStatus)}
                        reportDate={selectedReport?.date ? new Date(selectedReport.date) : new Date()}
                      />
                    ))}
                  </Box>
                )
              )
            }
          />
        </>
      )}
    </Box>
  );
};

export default DailyReports;